import { Fragment, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { splitMatches } from '@/lib/highlight';

interface HighlightTextProps {
  text: string | null | undefined;
  /** Case-insensitive term to mark. Leading/trailing whitespace is ignored. */
  query: string | null | undefined;
}

/**
 * Renders `text` with every occurrence of `query` wrapped in a `<mark>`, so a
 * search result shows why it matched. The mark colour follows the theme, so
 * it reads in both light and dark mode.
 */
export function HighlightText({ text, query }: HighlightTextProps): ReactNode {
  if (!text) return null;
  const parts = splitMatches(text, query ?? '');
  if (parts.length === 1 && !parts[0]?.match) return text;

  return parts.map((part, i) =>
    part.match ? (
      <Box
        key={i}
        component="mark"
        sx={(theme) => ({
          bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.22 : 0.35),
          color: 'inherit',
          fontWeight: 600,
          borderRadius: 0.5,
          px: '1px',
        })}
      >
        {part.text}
      </Box>
    ) : (
      <Fragment key={i}>{part.text}</Fragment>
    ),
  );
}
